/**
 * StudioCaptionCharCount — Platform-aware character count for the caption
 * editor.
 *
 * Extracted from the old ``StudioPlatformPreview`` so that character-limit
 * feedback lives next to the textarea the user is typing in, instead of
 * being buried in a separate "Platform Optimization" panel.
 *
 * Behavior:
 *   - When ``activeTab === 'all'``: shows the most restrictive limit
 *     across all selected platforms (e.g. if Twitter is selected, the
 *     280-char limit applies even though Instagram allows 2200).
 *   - When ``activeTab`` is a specific platform: shows that platform's
 *     limit.
 *   - Over-limit state is red + AlertTriangle.
 *   - Within-limit state is green + CheckCircle.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle } from 'lucide-react';

/** Character limits for the main caption per platform. Keep in sync with
 * backend PLATFORM_RULES. */
export const CAPTION_CHAR_LIMITS: Record<string, number> = {
  instagram: 2200,
  twitter: 280,
  facebook: 63206,
  tiktok: 2200,
  linkedin: 3000,
  youtube: 5000,
  pinterest: 500,
  bluesky: 300,
};

interface Props {
  /** Current caption text. */
  text: string;
  /** All platforms the post is targeting. */
  platforms: string[];
  /** The platform tab the user is editing ('all' means shared caption). */
  activeTab: string;
}

export default function StudioCaptionCharCount({
  text,
  platforms,
  activeTab,
}: Props) {
  const { t } = useTranslation();

  // Pick the relevant limit: either the active tab, or the tightest
  // limit across all selected platforms when editing the shared caption.
  const { limit, limitingPlatform } = useMemo<{
    limit: number | null;
    limitingPlatform: string | null;
  }>(() => {
    if (activeTab !== 'all') {
      return {
        limit: CAPTION_CHAR_LIMITS[activeTab] ?? null,
        limitingPlatform: activeTab,
      };
    }
    // When editing the shared caption, respect the tightest platform
    // limit so the user isn't surprised by Twitter truncation later.
    let tightest: number | null = null;
    let limitingId: string | null = null;
    for (const p of platforms) {
      const lim = CAPTION_CHAR_LIMITS[p];
      if (lim !== undefined && (tightest === null || lim < tightest)) {
        tightest = lim;
        limitingId = p;
      }
    }
    return { limit: tightest, limitingPlatform: limitingId };
  }, [activeTab, platforms]);

  if (!limit) {
    // No limit configured — just show the char count for reference.
    return (
      <div className="text-[10px] text-n-text-lt mt-1">
        {text.length} {t('studio_post_chars', 'chars')}
      </div>
    );
  }

  const ratio = text.length / limit;
  const over = text.length > limit;
  const near = ratio > 0.9 && !over;
  const barColor = over ? '#ef4444' : near ? '#f59e0b' : '#2383e2';

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between text-[10px]">
        <span className="uppercase tracking-wide text-n-text-lt font-medium">
          {activeTab === 'all' && limitingPlatform
            ? t(
                'studio_caption_charcount_tightest',
                'Char count ({{platform}} limit)',
                { platform: limitingPlatform },
              )
            : t('studio_caption_charcount', 'Char count')}
        </span>
        <span className="text-n-text-md">
          {text.length} / {limit}
        </span>
      </div>
      <div
        className="w-full h-1 bg-n-border/40 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.min(text.length, limit)}
        aria-valuemax={limit}
        aria-valuemin={0}
      >
        <div
          className="h-full rounded-full transition-all duration-[120ms]"
          style={{
            width: `${Math.min(ratio * 100, 100)}%`,
            backgroundColor: barColor,
          }}
        />
      </div>
      {over && (
        <div className="flex items-center gap-1 text-[10px] text-red-600">
          <AlertTriangle size={10} aria-hidden="true" />
          {t(
            'studio_caption_charcount_over',
            '{{count}} chars over limit',
            { count: text.length - limit },
          )}
        </div>
      )}
      {!over && text.length > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-green-600">
          <CheckCircle size={10} aria-hidden="true" />
          {t('studio_caption_charcount_within', 'Within limit')}
        </div>
      )}
    </div>
  );
}
